import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const ROLE_MAP = {
  SNP_DA: "SNP_DA", SNP_AR: "SNP_AR", SNP_HOS: "SNP_HOS",
  FNA_DA: "FNA_DA", FNA_AR: "FNA_AR", FNA_HOS: "FNA_HOS",
  INTERNAL_AUDITOR: "INTERNAL_AUDITOR", DEAN: "DEAN", DIRECTOR: "DIRECTOR"
};

function ApprovalQueue() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requisitions");
  const [requisitions, setRequisitions] = useState([]);
  const [indents, setIndents] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [payments, setPayments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const approvalLevel = ROLE_MAP[user?.role];

  useEffect(() => {
    if (approvalLevel) fetchPending();
    else setLoading(false);
  }, [approvalLevel]);

  const fetchPending = async () => {
    try {
      const [r, i, p, pay] = await Promise.all([
        axios.get(`${API_URL}/requisitions`, getAuthHeader()),
        axios.get(`${API_URL}/indents`, getAuthHeader()),
        axios.get(`${API_URL}/sanction-proposals`, getAuthHeader()),
        axios.get(`${API_URL}/payment-recommendations`, getAuthHeader())
      ]);
      setRequisitions(r.data.filter(x => x.status === "PENDING" && x.current_approval_level === approvalLevel));
      setIndents(i.data.filter(x => x.status === "PENDING" && x.current_approval_level === approvalLevel));
      setProposals(p.data.filter(x => x.status === "PENDING" && x.current_approval_level === approvalLevel));
      setPayments(pay.data.filter(x => x.status === "PENDING" && x.current_approval_level === approvalLevel));
    } catch (e) {
      toast.error("Failed to fetch");
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (type, id, action) => {
    setSubmitting(true);
    const endpoints = {
      requisition: `${API_URL}/requisitions/${id}/approve`,
      indent: `${API_URL}/indents/${id}/approve`,
      proposal: `${API_URL}/sanction-proposals/${id}/approve`,
      payment: `${API_URL}/payment-recommendations/${id}/approve`
    };
    try {
      await axios.put(endpoints[type], { action, remarks: "" }, getAuthHeader());
      toast.success(`${action.toLowerCase()}d`);
      fetchPending();
    } catch (e) {
      toast.error("Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!approvalLevel) {
    return (
      <div className="space-y-6" data-testid="approval-queue-page">
        <h1 className="text-2xl font-bold text-slate-900">Approval Queue</h1>
        <Card className="p-12 text-center"><p className="text-slate-500">You are logged in as Indenter. Approval queue is for approvers only.</p></Card>
      </div>
    );
  }

  const total = requisitions.length + indents.length + proposals.length + payments.length;
  const tabs = [
    { id: "requisitions", label: "Requisitions", count: requisitions.length },
    { id: "indents", label: "Indents", count: indents.length },
    { id: "proposals", label: "Proposals", count: proposals.length },
    { id: "payments", label: "Payments", count: payments.length }
  ];

  const renderItems = (items, type) => items.length > 0 ? items.map(item => (
    <Card key={item.id} className="mb-4" data-testid={`approval-card-${item.id}`}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <p className="font-semibold">{item.requisition_number || item.indent_number || item.proposal_number || item.recommendation_number}</p>
          <p className="text-sm text-slate-500">{item.item_description || item.tender_mode || item.basis_of_selection || item.item_name}</p>
          <p className="text-lg font-bold mt-1">₹{(item.estimated_value || item.final_price || item.invoice_amount || 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="sm" className="bg-emerald-600" onClick={() => handleApproval(type, item.id, "APPROVE")} disabled={submitting} data-testid={`approve-${item.id}`}>Approve</Button>
          <Button size="sm" variant="outline" onClick={() => handleApproval(type, item.id, "RETURN")} disabled={submitting}>Return</Button>
          <Button size="sm" variant="outline" className="text-rose-600" onClick={() => handleApproval(type, item.id, "REJECT")} disabled={submitting}>Reject</Button>
        </div>
      </CardContent>
    </Card>
  )) : <Card className="p-8 text-center text-slate-500">No pending items</Card>;

  return (
    <div className="space-y-6" data-testid="approval-queue-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Approval Queue</h1>
          <p className="text-slate-500">Level: {approvalLevel.replace('_', ' ')}</p>
        </div>
        <div className="px-4 py-2 bg-amber-50 rounded border border-amber-200 font-semibold text-amber-900">{total} pending</div>
      </div>

      <div className="flex gap-2 border-b">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 text-sm font-medium relative ${activeTab === t.id ? 'text-slate-900 border-b-2 border-slate-900' : 'text-slate-500'}`} data-testid={`tab-${t.id}`}>
            {t.label}
            {t.count > 0 && <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      <div>
        {loading ? <div className="p-8 text-center">Loading...</div> : (
          <>
            {activeTab === "requisitions" && renderItems(requisitions, "requisition")}
            {activeTab === "indents" && renderItems(indents, "indent")}
            {activeTab === "proposals" && renderItems(proposals, "proposal")}
            {activeTab === "payments" && renderItems(payments, "payment")}
          </>
        )}
      </div>
    </div>
  );
}

export default ApprovalQueue;
